import React, { useMemo } from 'react';
import styled from 'styled-components';
import ProgressGauge from 'components/atoms/ProgressGauge';
import { COLOR_SYSTEM } from 'components/design/design-system';
import InfoIconButton from 'components/atoms/InfoIconButton';
import Icon from 'components/atoms/Icon';
import { medicineCountLevel, GAUGE_GREEN_MAX } from './medicineCountStatus';

export interface MedicineCountGaugeProps {
  count: number;
  onCountExceed?: (count: number) => void;
  className?: string;
}

const WarningBox = styled.div<{ borderColor: string; backgroundColor: string }>`
  display: inline-flex;
  flex-direction: column;
  padding: 16px;
  border: 1px solid ${props => props.borderColor};
  border-radius: 8px;
  background-color: ${props => props.backgroundColor};
`;

const WarningHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const WarningTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")[800]};
`;

const WarningContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const CountInfo = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 8px;
`;

const CountLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${COLOR_SYSTEM.get("Gray")[600]};
`;

const CountValue = styled.span<{ color: string }>`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.color};
`;

const StatusMessage = styled.div<{ color: string }>`
  font-size: 13px;
  line-height: 1.4;
  color: ${props => props.color};
  margin-bottom: 4px;
`;

const ActionMessage = styled.div`
  font-size: 12px;
  line-height: 1.4;
  color: ${COLOR_SYSTEM.get("Gray")[700]};
  margin-bottom: 12px;
`;

const MedicineCountGauge: React.FC<MedicineCountGaugeProps> = ({
  count,
  onCountExceed,
  className
}) => {
  const actionText = "오래된 처방전을 삭제거나, 처방전 수정 버튼을 눌러 분석이 불필요한 약품을 삭제하세요.";

  const getStatusInfo = useMemo(() => {
    const level = medicineCountLevel(count);
    if (level === "none") {
      return null; // 안전 구간(≤39)은 표시하지 않음
    } else if (level === "yellow") {
      return {
        text: "약품 수가 많습니다. 분석은 아직 안정적이나 정리를 고려하세요.",
        color: COLOR_SYSTEM.get("Yellow")[700],
        borderColor: COLOR_SYSTEM.get("Yellow")[300],
        backgroundColor: COLOR_SYSTEM.get("Yellow")[10]
      };
    } else if (level === "orange") {
      return {
        text: "약품 수가 매우 많습니다. 분석 실패 위험이 있어 약 정리를 권장합니다.",
        color: COLOR_SYSTEM.get("Orange")[500],
        borderColor: COLOR_SYSTEM.get("Orange")[300],
        backgroundColor: COLOR_SYSTEM.get("Orange")[10]
      };
    } else {
      return {
        text: "분석 한계에 근접했습니다. 약을 정리해야 분석이 안정적입니다.",
        color: COLOR_SYSTEM.get("Red")[700],
        borderColor: COLOR_SYSTEM.get("Red")[400],
        backgroundColor: COLOR_SYSTEM.get("Red")[10]
      };
    }
  }, [count]);

  const tooltipContent = (
    <div>
      <div style={{ marginBottom: '2px', fontWeight: 'bold' }}>
        고유 약품 수는 중복을 제외한 약품의 총 개수를 의미합니다.
      </div>
      <div>
        🟢 초록 (0-39개): 정확하게 분석 가능
      </div>
      <div>
        🟡 노랑 (40-49개): 약품 많음(분석은 안정적)
      </div>
      <div>
        🟠 주황 (50-66개): 분석 실패 위험
      </div>
      <div style={{ marginBottom: '8px' }}>
        🔴 빨강 (67개 이상): 사실상 분석 불가
      </div>
      <div style={{ paddingTop: '8px', borderTop: `1px solid ${COLOR_SYSTEM.get("Gray")[600]}` }}>
        <strong>💡 우선순위 약물:</strong><br />
        • 현재 복용 중일 가능성이 높은 약물<br />
        • 만성질환 관리, 생명 유지 필수 약물 <br />
        • 신/간기능 영향 약물, 상호작용이 많은 약물<br />
        • PRN 투여 약물,  투여 주기가 긴 약물<br />
        • 계절성 약물, 자주 처방되는 약물<br />
      </div>
    </div>
  );

  const displayCount = Math.min(count, 100);

  React.useEffect(() => {
    if (count > GAUGE_GREEN_MAX && onCountExceed) {
      onCountExceed(count);
    }
  }, [count, onCountExceed]);

  // 안전 구간(≤39)은 렌더링하지 않음
  if (count <= GAUGE_GREEN_MAX) {
    return null;
  }

  return (
    <WarningBox 
      className={className} 
      borderColor={getStatusInfo?.borderColor || COLOR_SYSTEM.get("Gray")[300]}
      backgroundColor={getStatusInfo?.backgroundColor || COLOR_SYSTEM.get("Gray")[10]}
    >
      <WarningHeader>
        <Icon 
          name="Exclamation Mark Polygon Filled Filled" 
          size={20} 
          color={getStatusInfo?.color || COLOR_SYSTEM.get("Gray")[700]}
        />
        <WarningTitle>분석성능경고</WarningTitle>
        <InfoIconButton 
          tooltip={tooltipContent}
          iconSize={14}
          tooltipMaxWidth="320px"
        />
      </WarningHeader>
      <WarningContent>
        <CountInfo>
          <CountLabel>고유 약품 수</CountLabel>
          <CountValue color={getStatusInfo?.color || COLOR_SYSTEM.get("Gray")[700]}>
            {count}개
          </CountValue>
        </CountInfo>
        {getStatusInfo && (
          <StatusMessage color={getStatusInfo.color} role="status">
            {getStatusInfo.text}
          </StatusMessage>
        )}
        {getStatusInfo && (
          <ActionMessage>
            {actionText}
          </ActionMessage>
        )}
        <ProgressGauge
          value={displayCount}
          thresholds={[0, 40, 50, 67, 100]}
          colors={[COLOR_SYSTEM.get("Green")[400], COLOR_SYSTEM.get("Yellow")[400], COLOR_SYSTEM.get("Orange")[400], COLOR_SYSTEM.get("Red")[400]]}
          mode="even"
          showIndicator={true}
          width="100%"
          height="8px"
          gradientWidth={30}
        />
      </WarningContent>
    </WarningBox>
  );
};

export default MedicineCountGauge;